"use client";

import { Check, Close, KeyboardArrowRight } from "@ellaandil/ui";
import type { GeoLocale } from "@trydig/next/i18n";
import { DialogClose, Slot } from "@trydig/ui";
import { type ComponentProps, isValidElement, Suspense, useEffect, useState } from "react";
import { Translate } from "@/components/Translate";
import { Breadcrumbs } from "@/components/ui/Breadcrumbs";
import { Button } from "@/components/ui/Button";
import { DrawerContent, DrawerRoot, DrawerViewport } from "@/components/ui/Drawer";
import { Typography } from "@/components/ui/Typography";
import { CurrencyView } from "@/features/navigation/GeoSelector/CurrencyView";
import { useGeoPopup } from "@/features/navigation/GeoSelector/hooks";
import { useTranslations } from "@/i18n/client";
import { countries, routing, useGeo } from "@/i18n/routing";
import { useModal } from "@/lib/hooks/useModal";

type View = "root" | "language" | "currency";

export const GeoSelectorModal = () => {
	const { isOpen, setOpen } = useModal("geo");
	const { action, country: selectedCountry, language: selectedLanguage, pending } = useGeo();
	const { close } = useGeoPopup();
	const t = useTranslations("geo");

	const [view, setView] = useState<View>("root");
	const [pendingLanguage, setPendingLanguage] = useState(selectedLanguage);
	const [pendingCountry, setPendingCountry] = useState(selectedCountry);

	useEffect(() => {
		if (isOpen) {
			setView("root");
			setPendingLanguage(selectedLanguage);
			setPendingCountry(selectedCountry);
		}
	}, [isOpen, selectedLanguage, selectedCountry]);

	useEffect(() => {
		if (view === "language") setPendingLanguage(selectedLanguage);
		if (view === "currency") setPendingCountry(selectedCountry);
	}, [view, selectedLanguage, selectedCountry]);

	const currentCountry = countries.find((c) => c.country === selectedCountry);

	const sortedLanguages = [...routing.languages].sort((a, b) => {
		if (a === selectedLanguage) return -1;
		if (b === selectedLanguage) return 1;
		return 0;
	});

	const handleConfirm = () => {
		close();
		if (view === "language") {
			action(`${pendingLanguage}-${selectedCountry}` as GeoLocale);
		} else if (view === "currency") {
			action(`${selectedLanguage}-${pendingCountry}` as GeoLocale);
		}
	};

	const isLanguageDirty = view === "language" && pendingLanguage !== selectedLanguage;
	const isCurrencyDirty = view === "currency" && pendingCountry !== selectedCountry;
	const isDirty = isLanguageDirty || isCurrencyDirty;

	return (
		<DrawerRoot open={isOpen} onOpenChange={setOpen}>
			<DrawerViewport className="!max-w-[600px] bg-background-bg-sand-1 backdrop:bg-overlay-3 backdrop:backdrop-blur-[2.5px]">
				<DrawerContent className="!px-0 !gap-0">
					<header className="flex h-12 shrink-0 items-center px-grid-component-spacing-md">
						<div className="flex h-full flex-1 items-center justify-between gap-grid-component-spacing-md border-b border-border-medium">
							<Breadcrumbs
								size="small"
								disableSchema
								items={
									view === "root"
										? [{ label: t("title") }]
										: [
												{ label: t("title"), onClick: () => setView("root") },
												{ label: view === "language" ? t("language") : t("currency") },
											]
								}
							/>
							<DialogClose asChild>
								<button
									type="button"
									aria-label={t("close")}
									className="inline-flex min-h-9 min-w-9 cursor-pointer items-center justify-center p-1.5"
								>
									<Close className="size-6" />
								</button>
							</DialogClose>
						</div>
					</header>

					<div className="flex flex-1 flex-col gap-grid-component-spacing-2xl overflow-auto px-grid-component-spacing-md py-grid-component-spacing-2xl">
						{view === "root" && (
							<ul className="flex flex-col gap-grid-component-spacing-2xs">
								<li>
									<button
										type="button"
										onClick={() => setView("language")}
										className="flex w-full min-h-9 cursor-pointer flex-col items-stretch justify-center py-1.5 text-left outline-none"
									>
										<div className="flex w-full items-center justify-center gap-0">
											<Typography
												variant="heading-lg"
												as="span"
												className="flex-1 leading-[1.25] text-text-dark-text-1"
											>
												{t("language")}
											</Typography>
											<span className="inline-flex min-h-9 min-w-9 items-center justify-center p-1.5">
												<KeyboardArrowRight className="size-6" />
											</span>
										</div>
										<span className="block font-body font-regular text-2xs leading-[1.5] uppercase text-text-dark-text-2 tabular-nums lining-nums">
											<Translate>{`languages.${selectedLanguage}`}</Translate>
										</span>
									</button>
								</li>
								<li>
									<button
										type="button"
										onClick={() => setView("currency")}
										className="flex w-full min-h-9 cursor-pointer flex-col items-stretch justify-center py-1.5 text-left outline-none"
									>
										<div className="flex w-full items-center justify-center gap-0">
											<Typography
												variant="heading-lg"
												as="span"
												className="flex-1 leading-[1.25] text-text-dark-text-1"
											>
												{t("currency")}
											</Typography>
											<span className="inline-flex min-h-9 min-w-9 items-center justify-center p-1.5">
												<KeyboardArrowRight className="size-6" />
											</span>
										</div>
										<span className="block font-body font-regular text-2xs leading-[1.5] uppercase text-text-dark-text-2 tabular-nums lining-nums">
											<Translate>{`countries.${currentCountry?.name}`}</Translate> /{" "}
											{currentCountry?.currency}
										</span>
									</button>
								</li>
							</ul>
						)}

						{view === "language" && (
							<div className="flex flex-col gap-grid-section-vertical-md">
								<Typography
									variant="label-sm-reg"
									as="p"
									className="leading-[1.15] text-text-sand-text-4"
								>
									{t("changeLanguage")}
								</Typography>
								<ul className="flex flex-col gap-grid-component-spacing-2xs">
									{sortedLanguages.map((lang) => {
										const isSelected = pendingLanguage === lang;
										return (
											<li key={lang}>
												<button
													type="button"
													onClick={() => setPendingLanguage(lang)}
													className="flex w-full cursor-pointer items-center gap-0.5 py-0.5 text-left outline-none"
												>
													<span
														className={`flex-1 font-body font-regular text-sm leading-[1.5] ${
															isSelected ? "text-text-dark-text-2" : "text-text-dark-text-1"
														}`}
													>
														<Translate>{`languages.${lang}`}</Translate>
													</span>
													{isSelected && <Check className="size-5 text-text-dark-text-2" />}
												</button>
											</li>
										);
									})}
								</ul>
							</div>
						)}

						{view === "currency" && (
							<CurrencyView
								selectedCountry={selectedCountry}
								pendingCountry={pendingCountry}
								setPendingCountry={setPendingCountry}
							/>
						)}
					</div>

					{view !== "root" && (
						<div className="px-grid-component-spacing-md pb-grid-component-spacing-md">
							<div className="border-t border-border-medium pt-grid-component-spacing-md">
								<Button
									variant="solid"
									color="primary"
									size="large"
									className="w-full"
									onClick={handleConfirm}
									disabled={!isDirty || pending}
									loading={pending}
								>
									{t("confirm")}
								</Button>
							</div>
						</div>
					)}
				</DrawerContent>
			</DrawerViewport>
		</DrawerRoot>
	);
};

export interface GeoSelectorTriggerProps extends Omit<ComponentProps<"button">, "onClick"> {
	asChild?: boolean;
}

const UnsuspendedGeoSelectorTrigger = ({
	asChild,
	children,
	...props
}: GeoSelectorTriggerProps) => {
	const { toggle } = useModal("geo");

	if (asChild) {
		if (!isValidElement(children)) return null;
		return (
			<Slot {...props} onClick={toggle}>
				{children}
			</Slot>
		);
	}

	return (
		<button {...props} onClick={toggle}>
			{children}
		</button>
	);
};

export const GeoSelectorTrigger: typeof UnsuspendedGeoSelectorTrigger = (props) => {
	return (
		<Suspense>
			<UnsuspendedGeoSelectorTrigger {...props} />
		</Suspense>
	);
};
